import { useEffect, useRef, useState, useCallback } from "react";

interface UseDraftAutosaveOptions<T> {
  /** Unique key per builder, e.g. "draft:security-builder:user-123" */
  storageKey: string;
  /** The current state to persist */
  state: T;
  /** Called when restoring; return true to accept */
  onRestore: (saved: T) => void;
  /** Predicate: only save when this returns true (e.g. user has typed something) */
  enabled?: boolean;
  /** Debounce ms; defaults to 10s */
  intervalMs?: number;
}

/**
 * Auto-saves builder form state to localStorage every N seconds.
 * On mount, checks for a saved draft and exposes a `restorePrompt` flag.
 * Call `restoreDraft()` to apply, `dismissDraft()` to discard, `clearDraft()` after submit.
 */
export function useDraftAutosave<T>({
  storageKey,
  state,
  onRestore,
  enabled = true,
  intervalMs = 10000,
}: UseDraftAutosaveOptions<T>) {
  const [hasDraft, setHasDraft] = useState(false);
  const [draftAge, setDraftAge] = useState<string | null>(null);
  const savedRef = useRef<T | null>(null);
  const checkedRef = useRef(false);

  // On mount, check for an existing draft
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.data && parsed?.savedAt) {
        savedRef.current = parsed.data;
        setHasDraft(true);
        const ageMs = Date.now() - parsed.savedAt;
        const mins = Math.round(ageMs / 60000);
        if (mins < 1) setDraftAge("just now");
        else if (mins < 60) setDraftAge(`${mins} min ago`);
        else if (mins < 1440) setDraftAge(`${Math.round(mins / 60)} hr ago`);
        else setDraftAge(`${Math.round(mins / 1440)} day(s) ago`);
      }
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  // Periodic save
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ data: state, savedAt: Date.now() })
        );
      } catch {
        /* ignore quota errors */
      }
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [storageKey, state, enabled, intervalMs]);

  const restoreDraft = useCallback(() => {
    if (savedRef.current) {
      onRestore(savedRef.current);
    }
    setHasDraft(false);
  }, [onRestore]);

  const dismissDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    setHasDraft(false);
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  return { hasDraft, draftAge, restoreDraft, dismissDraft, clearDraft };
}
